import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BOT_LEVELS, opp, type Color, type Ruleset } from '@ic/engine';
import { GameView } from '../components/GameView';
import { Toggle } from '../components/Controls';
import { Icon } from '../components/Icons';
import { LeaveGuard } from '../components/LeaveGuard';
import { Loader } from '../components/Loader';
import { useLocalGame, type LocalMode } from '../lib/localGame';
import { activeRuleset, useStore } from '../lib/store';
import { canPlay, playableModes } from '../lib/modes';
import { auth } from '../lib/auth';
import { SignupWall } from '../components/SignupWall';
import { countGuestGame, useGuestQuota } from '../lib/guest';
import { parseTc, tcLabel } from './Play';

export function LocalGamePage({ sandbox = false }: { sandbox?: boolean }) {
  const params = useParams();
  const [q] = useSearchParams();
  const nav = useNavigate();
  const active = useStore(activeRuleset);
  const { admin } = useStore(auth);
  const mode: LocalMode = sandbox ? 'sandbox' : params.mode === 'bot' ? 'bot' : 'local';
  const ruleset = useMemo<Ruleset>(() => {
    if (sandbox) {
      try { const raw = sessionStorage.getItem('ic.sandbox'); if (raw) return JSON.parse(raw); } catch { /* fall through */ }
    }
    return active;
  }, [sandbox, active]);
  const level = Math.min(BOT_LEVELS.length, Math.max(1, Number(q.get('level') ?? 3)));
  const human: Color = q.get('color') === 'b' ? 'b' : 'w';
  const tc = useMemo(() => parseTc(q.get('tc')) ?? ruleset.rules.clock, [q, ruleset]);
  const botName = `Bot · ${BOT_LEVELS[level - 1].name}`;
  const names: Record<Color, string> = mode === 'bot'
    ? (human === 'w' ? { w: 'You', b: botName } : { w: botName, b: 'You' })
    : { w: 'White', b: 'Black' };

  const g = useLocalGame({ ruleset, mode, botLevel: level, humanColor: human, tc, names });
  // Guests get a few free games: counted at the first move, checked when the page opens.
  const quota = useGuestQuota();
  const lockedAtStart = useRef(quota.guest && quota.left === 0);
  const counted = useRef(false);
  useEffect(() => {
    if (g.entries.length <= 1) counted.current = false;
    else if (!counted.current && mode !== 'sandbox') { counted.current = true; countGuestGame(); }
  }, [g.entries.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [orientation, setOrientation] = useState<Color>(human);
  const [autoFlip, setAutoFlip] = useState(false);
  const shownOrientation = mode === 'local' && autoFlip ? g.state.turn : orientation;
  const takebacks = ruleset.rules.takebacks || mode === 'sandbox';
  const resignColor: Color = mode === 'bot' ? human : g.state.turn;
  const eventLog = useMemo(() => g.entries.flatMap((e) => e.events), [g.entries]);
  // Once the game is over, suggest a variant with pieces this one does not have.
  const newPieces = (x: Ruleset) => Object.values(x.pieces).filter((d) => !ruleset.pieces[d.id]).map((d) => d.name);
  const next = useMemo(() => {
    const others = playableModes().filter((x) => x.id !== ruleset.id);
    const fresh = others.filter((x) => newPieces(x).length > 0);
    const pool = fresh.length ? fresh : others;
    return pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
  }, [ruleset.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canPlay(ruleset)) return <Navigate to="/play/setup" replace />;
  if (lockedAtStart.current && mode !== 'sandbox') return <SignupWall />;
  const title = mode === 'sandbox' ? 'Test sandbox' : mode === 'bot' ? `vs ${botName}` : 'Pass & play';
  return (
    <div className="page game-page">
      <LeaveGuard
        active={mode !== 'sandbox' && !g.result && g.entries.length > 1}
        onLeave={() => g.forfeit(resignColor)}
        text={mode === 'bot' ? 'Leaving now counts as giving up: you lose this game against the bot.' : 'Leaving now ends this game: the player to move gives up.'}
      />
      <GameView
        title={title}
        subtitle={`${ruleset.name} · ${tc.initial > 0 && mode !== 'sandbox' ? tcLabel(tc) : 'untimed'}`}
        state={g.state}
        initial={g.initial}
        orientation={shownOrientation}
        legal={g.legal}
        interactive={!g.result}
        onAction={g.play}
        lastAction={g.lastAction}
        events={g.events}
        eventsKey={g.eventsKey}
        moves={g.moves}
        positions={g.positions}
        reviews={g.reviews}
        admin={admin && mode !== 'sandbox' ? { color: resignColor, onAction: (a) => g.admin(resignColor, a) } : null}
        result={g.result}
        viewer={mode === 'bot' ? human : null}
        eventLog={eventLog}
        next={next && mode !== 'sandbox' ? {
          name: next.name,
          desc: newPieces(next).length ? `New here: ${newPieces(next).slice(0, 3).join(', ')}` : next.description ?? '',
          onPlay: () => { activeRuleset.set(JSON.parse(JSON.stringify(next))); },
        } : null}
        ghost={g.pondering}
        celebrate={!!g.result?.winner && (mode === 'bot' ? g.result.winner === human : mode === 'local')}
        onFlip={() => { setAutoFlip(false); setOrientation(opp(shownOrientation)); }}
        players={{
          w: { name: names.w, clockMs: g.clock('w'), running: !g.result && g.state.turn === 'w' },
          b: { name: names.b, clockMs: g.clock('b'), running: !g.result && g.state.turn === 'b' },
        }}
        notice={<>
          {g.thinking && (
            <div className="notice thinking">
              <Loader size="sm" label={g.pondering ? <>{g.pondering.final ? 'Going with' : 'Considering'} <b className="mono">{g.ponderSan}</b></> : 'The bot is thinking…'} />
              {g.pondering && <span className="muted small">searched {g.pondering.depth} move{g.pondering.depth > 1 ? 's' : ''} deep</span>}
            </div>
          )}
          {mode === 'sandbox' && <div className="notice">Sandbox: you move both sides, all mines are visible, unlimited undo. <Link to="/editor">Back to editor</Link></div>}
        </>}
        controls={<>
          {takebacks && <button className="btn secondary" onClick={g.undo} disabled={g.entries.length < 2}><Icon name="undo" size={16} /> Takeback</button>}
          {!g.result && mode !== 'sandbox' && <button className="btn secondary" onClick={g.draw}>Offer draw</button>}
          {!g.result && mode !== 'sandbox' && <button className="btn danger" onClick={() => g.resign(resignColor)}><Icon name="flag" size={16} /> Resign</button>}
          {mode === 'local' && <div className="grow"><Toggle checked={autoFlip} onChange={setAutoFlip} label="Auto-flip board" hint="Turn the board to face the player to move." /></div>}
          <button className="btn ghost" onClick={g.reset}><Icon name="restart" size={16} /> Restart</button>
        </>}
        endActions={<>
          <button className="btn primary" onClick={g.reset}>Rematch</button>
          <button className="btn secondary" onClick={() => nav('/play/setup')}>New game</button>
        </>}
      />
    </div>
  );
}
